import { useMutation, useQuery } from '@apollo/client';
import { ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { useState } from 'react';

import {
  GET_ACCREDITATION_REVIEW,
  REVIEW_ACCREDITATION,
} from '@/apollo/schemas/customers/accreditationReview';
import { AdminActionWrapper } from '@/components/common/AdminActionWrapper';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/CoginitoAuthProvider';
import { useToast } from '@/hooks/use-toast';
import { docusignEnvelopeUrl } from '@/utils/docusign';

import { StatusPill } from './AccreditationReviewCell';

type Decision = 'APPROVED' | 'DENIED';

type ReviewData = {
  letterStatus: string;
  letterEnvelopeId?: string | null;
  signedAt?: string | null;
  /** The licensed professional the investor named, who signs the letter. */
  verifierName?: string | null;
  verifierEmail?: string | null;
  reviewStatus: 'PENDING' | 'APPROVED' | 'DENIED';
  reviewedBy?: string | null;
  reviewedAt?: string | null;
  reviewNote?: string | null;
  agreementEnvelopeId?: string | null;
  ace: string;
  registry: string;
  registryExplorerUrl?: string | null;
  canReview: boolean;
  reason?: string | null;
  rejections?: Array<{
    envelopeId: string;
    signedAt?: string | null;
    verifierName?: string | null;
    verifierEmail?: string | null;
    reviewedBy?: string | null;
    reviewedAt: string;
    reviewNote?: string | null;
  }> | null;
};

const PART_LABEL: Record<string, string> = {
  CONFIRMED: 'Confirmed',
  NOT_REQUIRED: 'Not required',
  IN_PROGRESS: 'In progress',
  NOT_STARTED: 'Not started',
  FAILED: 'Failed',
};

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleString() : '-';

const Row = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="grid grid-cols-[180px_1fr] gap-3 py-2 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <div className="min-w-0">{children}</div>
  </div>
);

/**
 * Opens the envelope in the DocuSign web app in a new tab. DocuSign asks for its own
 * sign-in there, so only people with access to the sending account can see the document.
 */
export const EnvelopeLink = ({
  envelopeId,
}: {
  envelopeId?: string | null;
}) => {
  if (!envelopeId) return <span className="text-muted-foreground">-</span>;
  const url = docusignEnvelopeUrl(envelopeId);
  return (
    <div className="flex flex-col gap-0.5">
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex w-fit items-center gap-1 font-medium text-primary hover:underline"
        >
          Open in DocuSign <ExternalLink className="h-3.5 w-3.5" />
        </a>
      ) : (
        <span className="text-xs text-muted-foreground">
          DocuSign link not configured for this environment
        </span>
      )}
      <span className="break-all font-mono text-[11px] text-muted-foreground">
        {envelopeId}
      </span>
    </div>
  );
};

/**
 * The investor's accreditation on the customer page: the signed letter (with a link to it
 * in DocuSign), the admin's Approve/Deny, the on-chain parts, and earlier denied letters.
 *
 * Approve/Deny appear once the letter and an agreement are signed. Nothing is on-chain before
 * the decision:
 *  - Approve records the accreditation on-chain (ACE credential + investment record); the
 *    investor's funding opens once both are confirmed.
 *  - Deny writes nothing on-chain. The investor is emailed and, next time they open their
 *    investment or any offering, signs a new letter, which comes back here for review. Their
 *    investment and signed agreement are kept.
 * An approval can still be changed to a denial (the ACE credential is then revoked).
 */
const AccreditationReviewPanel = ({
  customerId,
  customerName,
}: {
  customerId: string;
  customerName: string;
}) => {
  const { toast } = useToast();
  const { session } = useAuth();
  const [decision, setDecision] = useState<Decision | null>(null);
  const [note, setNote] = useState('');

  const headers = {
    identity: session
      ? session.getIdToken().getJwtToken()
      : localStorage.getItem('idToken'),
  };

  const { data, loading, error, refetch } = useQuery(GET_ACCREDITATION_REVIEW, {
    variables: { customerId },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    context: { headers },
  });
  const review: ReviewData | undefined = data?.getAccreditationReview?.data;

  const [submit, { loading: saving }] = useMutation(REVIEW_ACCREDITATION, {
    onCompleted: (res) => {
      const result = res?.reviewAccreditation;
      if (result?.statusCode === 200) {
        toast({
          variant: 'success',
          title: result.message,
          description: customerName,
        });
        setDecision(null);
        setNote('');
        refetch();
      } else {
        toast({
          variant: 'destructive',
          title: 'Review not saved',
          description: result?.message ?? 'An unexpected error occurred.',
        });
      }
    },
    onError: (err) =>
      toast({
        variant: 'destructive',
        title: 'Review not saved',
        description: err.message,
      }),
  });

  const confirm = () =>
    decision &&
    submit({
      variables: { customerId, decision, note: note || null },
      context: { headers },
    });

  const openDialog = (d: Decision) => {
    setNote('');
    setDecision(d);
  };

  const approved = review?.reviewStatus === 'APPROVED';
  const rejected = review?.letterStatus === 'REJECTED';
  const decided = approved || review?.reviewStatus === 'DENIED';
  const onChainLabel = (part: string) =>
    approved ? (PART_LABEL[part] ?? part) : 'After approval';

  return (
    <div className="rounded-lg border bg-card p-4 shadow-sm">
      <div className="mb-2 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Accreditation</h2>
          <p className="text-sm text-muted-foreground">
            Accreditation letter, admin decision and on-chain record.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={loading}
        >
          <RefreshCw
            className={`mr-2 h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`}
          />
          Check again
        </Button>
      </div>

      {loading && !review ? (
        <p className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking accreditation
          and on-chain status…
        </p>
      ) : error || !review ? (
        <p className="py-4 text-sm text-destructive">
          {error?.message ??
            data?.getAccreditationReview?.message ??
            'Could not load the accreditation.'}
        </p>
      ) : (
        <>
          <div className="divide-y">
            <Row label="Letter">
              {review.letterStatus === 'NOT_STARTED' ? (
                <span className="text-muted-foreground">Not sent yet</span>
              ) : (
                <div className="flex items-center gap-2">
                  <StatusPill value={review.letterStatus} />
                  {review.signedAt && (
                    <span className="text-xs text-muted-foreground">
                      Signed {formatDate(review.signedAt)}
                    </span>
                  )}
                </div>
              )}
            </Row>
            <Row label="Verifier">
              {review.verifierName || review.verifierEmail ? (
                <div className="flex flex-col">
                  <span>{review.verifierName ?? '-'}</span>
                  <span className="text-xs text-muted-foreground">
                    {review.verifierEmail ?? ''}
                  </span>
                </div>
              ) : (
                <span className="text-muted-foreground">-</span>
              )}
            </Row>
            <Row label="Accreditation letter">
              <EnvelopeLink envelopeId={review.letterEnvelopeId} />
            </Row>
            <Row label="Subscription agreement">
              <EnvelopeLink envelopeId={review.agreementEnvelopeId} />
            </Row>
            <Row label="ACE credential">{onChainLabel(review.ace)}</Row>
            <Row label="On-chain record">
              <div className="flex items-center gap-2">
                <span>{onChainLabel(review.registry)}</span>
                {review.registryExplorerUrl && (
                  <a
                    href={review.registryExplorerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    View transaction <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </Row>
            <Row label="Decision">
              {rejected ? (
                <span className="text-muted-foreground">
                  Denied. Waiting for the investor to send a new letter to their
                  verifier.
                </span>
              ) : decided ? (
                <div className="flex flex-col gap-1">
                  <StatusPill value={review.reviewStatus} />
                  <span className="text-xs text-muted-foreground">
                    {review.reviewedBy ? `By ${review.reviewedBy} · ` : ''}
                    {formatDate(review.reviewedAt)}
                  </span>
                  {review.reviewNote && (
                    <span className="text-xs">Note: {review.reviewNote}</span>
                  )}
                </div>
              ) : (
                <span className="text-muted-foreground">
                  {review.canReview
                    ? 'Awaiting review'
                    : (review.reason ?? 'Not ready for review')}
                </span>
              )}
            </Row>
          </div>

          {!!review.rejections?.length && (
            <div className="mt-4">
              <p className="mb-1 text-sm font-medium">
                Earlier letters (denied)
              </p>
              <div className="divide-y rounded-md border">
                {review.rejections.map((r) => (
                  <div
                    key={r.envelopeId}
                    className="grid grid-cols-[180px_1fr] gap-3 p-2 text-sm"
                  >
                    <span className="text-muted-foreground">
                      Denied {formatDate(r.reviewedAt)}
                      {r.reviewedBy ? ` by ${r.reviewedBy}` : ''}
                    </span>
                    <div className="min-w-0">
                      <EnvelopeLink envelopeId={r.envelopeId} />
                      {(r.verifierName || r.verifierEmail) && (
                        <span className="block text-xs text-muted-foreground">
                          Verifier: {r.verifierName ?? ''}
                          {r.verifierEmail ? ` (${r.verifierEmail})` : ''}
                        </span>
                      )}
                      {r.reviewNote && (
                        <span className="text-xs">Note: {r.reviewNote}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {review.canReview && (
            <div className="mt-4 flex gap-2">
              {approved ? (
                <AdminActionWrapper>
                  <Button
                    variant="outline"
                    onClick={() => openDialog('DENIED')}
                  >
                    Change to deny
                  </Button>
                </AdminActionWrapper>
              ) : (
                <>
                  <AdminActionWrapper>
                    <Button
                      className="bg-emerald-600 text-white hover:bg-emerald-700"
                      onClick={() => openDialog('APPROVED')}
                    >
                      Approve
                    </Button>
                  </AdminActionWrapper>
                  <AdminActionWrapper>
                    <Button
                      variant="outline"
                      className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
                      onClick={() => openDialog('DENIED')}
                    >
                      Deny
                    </Button>
                  </AdminActionWrapper>
                </>
              )}
            </div>
          )}
        </>
      )}

      <Dialog
        open={!!decision}
        onOpenChange={(open) => !open && setDecision(null)}
      >
        <DialogContent className="sm:max-w-[440px] bg-white dark:bg-foreground dark:text-white">
          <DialogHeader>
            <DialogTitle>
              {decision === 'APPROVED'
                ? 'Approve accreditation'
                : 'Deny accreditation'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{customerName}</span>{' '}
            {decision === 'APPROVED'
              ? 'will have their accreditation recorded on-chain (ACE credential and investment record). Their funding details open once that is confirmed, for this and future investments.'
              : approved
                ? 'will be emailed that their letter was not approved and asked to sign a new one. Their ACE accreditation credential is revoked; on-chain investment records already written stay. Investments already funded are not affected.'
                : 'will be emailed that their letter was not approved. Next time they open their investment or any offering they send a new letter to their verifier, which comes back here for review once signed. Their investment and signed agreement are kept, and nothing is written on-chain.'}
          </p>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Internal note (optional, not shown to the investor)"
            rows={3}
          />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDecision(null)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={confirm}
              disabled={saving}
              className={
                decision === 'APPROVED'
                  ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                  : 'bg-red-600 text-white hover:bg-red-700'
              }
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {decision === 'APPROVED' ? 'Approve' : 'Deny'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccreditationReviewPanel;
